import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { useRaidContext } from '../../context/RaidContext';

import GeneralRisksFilters from './GeneralRisksFilters';
import GeneralRisksTable from './GeneralRisksTable';
import GeneralRiskUpsertModal from './GeneralRiskUpsertModal';
import TrashedGeneralRisksTable from './TrashedGeneralRisksTable';

import Button from 'CommonComponents/Button';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

export const UPSERT_RISK_MODAL_ID = 'upsert-general-risk-modal';

export default function GeneralRisksIndex({ handleCreated }) {
    const [isLoading, setIsLoading] = useState(false);

    const {
        currentVenue,
        isMasterSource,
        risks,
        trashedRisks,
        issues,
        decisions,
        types,
        impacts,
        impactCategories,
        probabilities,
        statuses,
        departmentsOptions,
        venuesOptions,
        canManageRisks,
        fetchGeneralRisks,
        fetchTrashedGeneralRisks,
        routes,
        getQuery,
        fetchDatasets,
        modalsVisibility,
        toggleModalVisibility,
        selectedRisk,
        setSelectedRisk,
        loadingDatasets,
    } = useRaidContext();

    const openRisks = useMemo(() => risks.filter((risk) => !risk.is_closed), [risks]);
    const closedRisks = useMemo(() => risks.filter((risk) => risk.is_closed), [risks]);

    const riskModal = useModal();

    const toast = useToast();

    const confirm = useConfirmationModal();

    useEffect(() => {
        setIsLoading(true);
        const requests = [fetchGeneralRisks()];
        if (isMasterSource) {
            requests.push(fetchTrashedGeneralRisks());
        }
        Promise.all(requests)
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        const currentId = getQuery().type === 'risk' ? getQuery().id : null;
        if (currentId) {
            const risk = risks.find((r) => r.id === parseInt(currentId));
            if (risk) {
                setSelectedRisk(risk);
                riskModal.show();
            }
        }
    }, [risks]);

    useEffect(() => {
        if (modalsVisibility[UPSERT_RISK_MODAL_ID]) {
            riskModal.show();
        } else {
            riskModal.hide();
        }
    }, [modalsVisibility[UPSERT_RISK_MODAL_ID]]);

    const pushUrlToOriginal = () => {
        const query = getQuery();
        if (query.type && history.pushState) {
            const newurl = window.location.href.split('?')[0];
            window.history.pushState({ path: newurl }, '', newurl);
        }
    };

    const onCreated = () => {
        pushUrlToOriginal();
        fetchGeneralRisks();

        if (handleCreated && typeof handleCreated === 'function') {
            handleCreated();
        }
    };

    const onClose = () => {
        const query = getQuery();
        if (query.type && history.pushState) {
            pushUrlToOriginal();
            fetchDatasets();
        }
        toggleModalVisibility(UPSERT_RISK_MODAL_ID, false);
    };

    const onEdit = (model) => {
        if (!canManageRisks) {
            return;
        }
        setSelectedRisk(model);
        toggleModalVisibility(UPSERT_RISK_MODAL_ID, true);
    };

    const deleteRisk = (model) => {
        const deleteUrl = routes.deleteGeneralRiskUrl.replace('GENERAL_RISK_ID', model.id);

        axios
            .delete(deleteUrl, { params: { venue_id: currentVenue?.id } })
            .then(({ data }) => {
                fetchGeneralRisks();
                if (isMasterSource) {
                    fetchTrashedGeneralRisks();
                }

                if (data?.status === 'ok' && data?.message) {
                    toast.success(data.message);
                }
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const onDelete = (model) => {
        if (!canManageRisks) {
            return;
        }

        confirm({
            title: $t('Delete Risk'),
            message: $t(
                'Are you sure you want to delete this risk? If this risk has a budget impact attached to a budget line it may take a few minutes to be reflected in the department. Alternatively, press the Recalculate Totals button on the Budget Dashboard.',
            ),
            onConfirm: () => {
                deleteRisk(model);

                if (modalsVisibility[UPSERT_RISK_MODAL_ID]) {
                    riskModal.hide();
                }
            },
        });
    };

    const onRestore = (model) => {
        if (!canManageRisks) {
            return;
        }

        const restoreUrl = routes.restoreGeneralRiskUrl.replace('GENERAL_RISK_ID', model.id);

        axios
            .post(restoreUrl)
            .then(({ data }) => {
                fetchGeneralRisks();
                fetchTrashedGeneralRisks();

                if (data?.status === 'ok' && data?.message) {
                    toast.success(data.message);
                }
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const onForceDelete = (model) => {
        if (!canManageRisks) {
            return;
        }

        confirm({
            title: $t('Permanently Delete Risk'),
            message: $t('Are you sure you want to permanently delete this risk? This action cannot be undone.'),
            onConfirm: () => {
                const forceDeleteUrl = routes.forceDeleteGeneralRiskUrl.replace('GENERAL_RISK_ID', model.id);

                axios
                    .delete(forceDeleteUrl)
                    .then(({ data }) => {
                        fetchTrashedGeneralRisks();

                        if (data?.status === 'ok' && data?.message) {
                            toast.success(data.message);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            },
        });
    };

    const onStar = (model) => {
        if (!canManageRisks) {
            return;
        }
        const url = routes.updateGeneralRiskHotStatusUrl.replace('GENERAL_RISK_ID', model.id);
        axios.put(url, { is_hot: !model.is_hot }).then(() => fetchGeneralRisks());
    };

    return (
        <Fragment>
            <div className="table__card tw-space-y-8">
                <div>
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                        <div>
                            <h4 className="table__header">{$t('Risks')}</h4>
                        </div>
                        <div className="tw-flex tw-gap-x-1.5">
                            <Button variant="primary" as="a" href={routes.generalRisksExportUrl}>
                                {$t('Download')}
                            </Button>
                            <GeneralRisksFilters
                                datasets={{ types, impactCategories, venuesOptions, departmentsOptions }}
                                showSourceFilter={isMasterSource}
                                onSubmit={(values) => fetchGeneralRisks(values)}
                                onReset={() => fetchGeneralRisks()}
                            />
                        </div>
                    </div>
                    <GeneralRisksTable
                        showSourceColumn={isMasterSource}
                        isLoading={isLoading}
                        risks={openRisks}
                        impactCategories={impactCategories}
                        showUrl={routes.fetchGeneralRiskUrl}
                        reorderUrl={routes.reorderGeneralRiskUrl}
                        onStar={(risk) => onStar(risk)}
                        onEdit={(risk) => onEdit(risk)}
                        onDelete={(risk) => onDelete(risk)}
                        canManage={canManageRisks}
                    />
                </div>
                <div>
                    <h4 className="table__header tw-mb-6">{$t('Closed Risks')}</h4>
                    <GeneralRisksTable
                        showSourceColumn={isMasterSource}
                        isLoading={isLoading}
                        risks={closedRisks}
                        impactCategories={impactCategories}
                        showUrl={routes.fetchGeneralRiskUrl}
                        reorderUrl={routes.reorderGeneralRiskUrl}
                        onStar={(risk) => onStar(risk)}
                        onEdit={(risk) => onEdit(risk)}
                        onDelete={(risk) => onDelete(risk)}
                        canManage={canManageRisks}
                    />
                </div>
                {canManageRisks && isMasterSource && (
                    <div>
                        <h4 className="table__header tw-mb-6">{$t('Deleted Risks')}</h4>
                        <TrashedGeneralRisksTable
                            isLoading={isLoading}
                            risks={trashedRisks}
                            onRestore={(risk) => onRestore(risk)}
                            onForceDelete={(risk) => onForceDelete(risk)}
                        />
                    </div>
                )}
            </div>

            <GeneralRiskUpsertModal
                id={UPSERT_RISK_MODAL_ID}
                isLoading={loadingDatasets}
                currentVenue={currentVenue}
                allowVenueSelection={isMasterSource}
                datasets={{ types, impacts, impactCategories, probabilities, statuses, issues, decisions, departmentsOptions, venuesOptions }}
                getPriorityUrl={routes.getGeneralRiskPriorityUrl}
                storeUrl={routes.storeGeneralRiskUrl}
                updateUrl={routes.updateGeneralRiskUrl}
                fetchGeneralRiskBudgetLinesUrl={routes.fetchGeneralRiskBudgetLinesUrl}
                modal={riskModal}
                risk={selectedRisk}
                budget_department_type={getQuery().budget_department_type}
                budget_department_id={getQuery().budget_department_id}
                onCreated={() => onCreated()}
                onClose={() => onClose()}
                canManage={canManageRisks}
                onDelete={() => onDelete(selectedRisk)}
            />
        </Fragment>
    );
}
